// src/pages/Marketplace/markets/MarketDetailPanel.tsx
import React, { useEffect, useState } from 'react';
import { ErrorBanner, SuccessBanner } from '@/pages/Admin/shared';
import type { Env } from '@/pages/Admin/shared';
import type { Market } from '@/pages/Marketplace/shared';
import { useMarketMutations } from '@/pages/Marketplace/shared';
import MarketMapPreview from './MarketMapPreview';
import BusinessesInMarket from './BusinessesInMarket';
import SpotlightPanel from './SpotlightPanel';

interface Props {
  market: Market;
  apiUrl: string;
  /**
   * The spotlight hooks resolve their own base URL and token from `env` via
   * `useAdminApi` (it also adds the non-prod WAF header), so they take `env`
   * rather than the `apiUrl` / `accessToken` pair the older hooks here use.
   */
  env: Env;
  accessToken: string | null;
  onChanged: () => void;
}

type Form = {
  name: string;
  is_active: boolean;
  timezone: string;
  college_name: string;
  center_lat: string; center_lng: string; radius_km: string;
  city: string; state: string;
};

function toForm(m: Market): Form {
  return {
    name: m.name,
    is_active: m.is_active,
    timezone: m.timezone,
    college_name: m.college_name ?? '',
    center_lat: m.center_lat != null ? String(m.center_lat) : '',
    center_lng: m.center_lng != null ? String(m.center_lng) : '',
    radius_km:  m.radius_km  != null ? String(m.radius_km)  : '',
    city: m.city ?? '',
    state: m.state ?? '',
  };
}

export default function MarketDetailPanel({ market, apiUrl, env, accessToken, onChanged }: Props): React.ReactElement {
  const [form, setForm] = useState<Form>(() => toForm(market));
  const [saved, setSaved] = useState(false);
  const { loading, error, update, softDelete } = useMarketMutations(apiUrl, accessToken);

  useEffect(() => { setForm(toForm(market)); setSaved(false); }, [market]);

  const dirty = JSON.stringify(form) !== JSON.stringify(toForm(market));

  const handleSave = async () => {
    const body: Parameters<typeof update>[1] = {
      name: form.name,
      is_active: form.is_active,
      timezone: form.timezone,
    };
    if (market.kind === 'college') {
      body.college_name = form.college_name;
      body.center_lat = parseFloat(form.center_lat);
      body.center_lng = parseFloat(form.center_lng);
      body.radius_km = parseFloat(form.radius_km);
    } else {
      body.city = form.city;
      body.state = form.state;
    }
    const result = await update(market.id, body);
    if (result) { setSaved(true); onChanged(); }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Soft-delete ${market.name}? Rejects if businesses are still assigned.`)) return;
    const ok = await softDelete(market.id);
    if (ok) onChanged();
  };

  const handleTzChange = (tz: string) => {
    if (tz === form.timezone) return;
    if (!window.confirm(
      'Changing timezone affects how existing surge events map to wall-clock times. Continue?',
    )) return;
    setForm((f) => ({ ...f, timezone: tz }));
  };

  return (
    <div className="max-w-3xl">
      <div className="flex items-center justify-between">
        <input
          className="text-2xl font-semibold bg-transparent text-white outline-none border-b border-transparent focus:border-violet-600 px-1"
          value={form.name}
          onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
        />
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.is_active}
              onChange={(e) => setForm((f) => ({ ...f, is_active: e.target.checked }))}
            />
            Active
          </label>
          <button
            className="text-xs px-3 py-1.5 rounded bg-red-950/40 border border-red-800/60 text-red-300 hover:bg-red-900/60"
            onClick={handleDelete}
          >
            Delete
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 mt-4">
        <div>
          <label className="text-xs text-gray-400">Kind (read-only)</label>
          <div className="text-sm mt-0.5">{market.kind}</div>
        </div>
        <div>
          <label className="text-xs text-gray-400">Timezone (IANA)</label>
          <select
            value={form.timezone}
            onChange={(e) => handleTzChange(e.target.value)}
            className="w-full bg-gray-900 border border-gray-700 rounded px-2 py-1 text-sm mt-0.5"
          >
            {Intl.supportedValuesOf('timeZone').map((tz) => (
              <option key={tz} value={tz}>{tz}</option>
            ))}
          </select>
        </div>

        {market.kind === 'college' ? (
          <>
            <div className="col-span-2">
              <label className="text-xs text-gray-400">College name</label>
              <input
                className="w-full bg-gray-900 border border-gray-700 rounded px-2 py-1 text-sm mt-0.5"
                value={form.college_name}
                onChange={(e) => setForm((f) => ({ ...f, college_name: e.target.value }))}
              />
            </div>
            <div>
              <label className="text-xs text-gray-400">Center lat</label>
              <input
                type="number" step="any"
                className="w-full bg-gray-900 border border-gray-700 rounded px-2 py-1 text-sm mt-0.5"
                value={form.center_lat}
                onChange={(e) => setForm((f) => ({ ...f, center_lat: e.target.value }))}
              />
            </div>
            <div>
              <label className="text-xs text-gray-400">Center lng</label>
              <input
                type="number" step="any"
                className="w-full bg-gray-900 border border-gray-700 rounded px-2 py-1 text-sm mt-0.5"
                value={form.center_lng}
                onChange={(e) => setForm((f) => ({ ...f, center_lng: e.target.value }))}
              />
            </div>
            <div>
              <label className="text-xs text-gray-400">Radius (km)</label>
              <input
                type="number" step="0.1" min="0"
                className="w-full bg-gray-900 border border-gray-700 rounded px-2 py-1 text-sm mt-0.5"
                value={form.radius_km}
                onChange={(e) => setForm((f) => ({ ...f, radius_km: e.target.value }))}
              />
            </div>
          </>
        ) : (
          <>
            <div>
              <label className="text-xs text-gray-400">City</label>
              <input
                className="w-full bg-gray-900 border border-gray-700 rounded px-2 py-1 text-sm mt-0.5"
                value={form.city}
                onChange={(e) => setForm((f) => ({ ...f, city: e.target.value }))}
              />
            </div>
            <div>
              <label className="text-xs text-gray-400">State</label>
              <input
                className="w-full bg-gray-900 border border-gray-700 rounded px-2 py-1 text-sm mt-0.5"
                value={form.state}
                onChange={(e) => setForm((f) => ({ ...f, state: e.target.value }))}
              />
            </div>
          </>
        )}
      </div>

      {market.kind === 'college' && (
        <div className="mt-4">
          <label className="text-xs text-gray-400">Map preview</label>
          <MarketMapPreview
            lat={parseFloat(form.center_lat) || null}
            lng={parseFloat(form.center_lng) || null}
            radiusKm={parseFloat(form.radius_km) || null}
          />
        </div>
      )}

      <div className="mt-4 flex items-center gap-3">
        <button
          disabled={!dirty || loading}
          onClick={handleSave}
          className="px-4 py-2 text-sm font-medium bg-violet-600 hover:bg-violet-700 disabled:opacity-40 rounded"
        >
          {loading ? 'Saving…' : 'Save changes'}
        </button>
        {saved && <SuccessBanner message="Saved." />}
        {error && <ErrorBanner message={error} />}
      </div>

      <SpotlightPanel market={market} env={env} />

      <BusinessesInMarket market={market} apiUrl={apiUrl} accessToken={accessToken} />
    </div>
  );
}
